import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Unified Back navigation control (DEV-003E).
 *
 * One component, one visual, one behaviour across Customer, Installer
 * and Operations on Mobile / iPad / Web. Use this **everywhere** — no
 * bespoke back buttons.
 *
 * Behaviour
 * ─────────
 * • If `to` is provided, always navigates deterministically to that
 *   path (React Router `navigate(to)`). Use this for pages whose
 *   parent is deterministic — e.g. a detail screen that must always
 *   return to its list — where relying on browser history would
 *   create a loop (list → detail → list → detail…).
 * • Otherwise clicks fire `navigate(-1)` when there's genuine session
 *   history (`window.history.state?.idx > 0`).
 * • When there isn't (e.g. the page was deep-linked or reloaded) it
 *   falls back to `fallbackHref` — pass the role-appropriate dashboard.
 *
 * Visual contract
 * ───────────────
 * • Left-hand ArrowLeft icon (lucide-react, 1.75 stroke).
 * • Label — either explicit via `label` prop, or the sensible default
 *   "Back". If the caller passes `label={null}` the component renders
 *   an icon-only round chip (used inside detail headers).
 * • Neutral chrome (`bg-white`, `border-[#E5E7EB]`, `text-[#131B2F]`)
 *   so it reads on every shell background.
 *
 * Placement
 * ─────────
 * • Always the first interactive element inside a page header, above
 *   or beside the page title.
 * • Never show on root screens (Landing, `/dashboard`, `/installer`,
 *   `/operations`, `/login`). Non-root screens must always render one.
 */
export default function BackButton({
    label,
    to,
    fallbackHref = "/",
    size = "pill",
    className = "",
    testid = "back-button",
}) {
    const navigate = useNavigate();
    const location = useLocation();

    const goBack = () => {
        if (to) {
            // Deterministic parent — always land here regardless of history.
            navigate(to);
            return;
        }
        const hasHistory = (window.history.state && window.history.state.idx > 0)
            || window.history.length > 1;
        if (hasHistory) {
            navigate(-1);
        } else {
            navigate(fallbackHref);
        }
    };

    // Icon-only variant — used inside compact detail headers (e.g. the
    // circular chip on the InstallerProject page). Kept for legacy call
    // sites but discouraged for new code.
    if (label === null || size === "icon") {
        return (
            <button
                type="button"
                onClick={goBack}
                data-testid={testid}
                aria-label="Go back"
                className={`w-10 h-10 rounded-full bg-white border border-[#E5E7EB] flex items-center justify-center text-[#131B2F] hover:bg-[#F5F5F7] transition-colors ${className}`}
            >
                <ArrowLeft size={16} strokeWidth={1.75} />
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={goBack}
            data-testid={testid}
            aria-label={label || "Back"}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7] transition-colors ${className}`}
        >
            <ArrowLeft size={14} strokeWidth={1.75} />
            <span>{label || "Back"}</span>
        </button>
    );
}
